import { useState } from "react";
import { Outlet, NavLink, useLocation } from "react-router-dom";
import { 
  LayoutDashboard, 
  ArrowRightLeft, 
  ScrollText, 
  Settings, 
  Key, 
  Activity,
  Menu,
  X,
  Server,
  Zap
} from "lucide-react";
import { Button } from "../ui/button";
import { cn } from "../../lib/utils";

const navigation = [
  { name: "Dashboard", href: "/dashboard", icon: LayoutDashboard },
  { name: "Fluxos", href: "/flows", icon: ArrowRightLeft },
  { name: "Monitor", href: "/monitor", icon: Activity },
  { name: "Logs", href: "/logs", icon: ScrollText },
  { name: "API Keys", href: "/api-keys", icon: Key },
  { name: "Configurações", href: "/settings", icon: Settings },
];

export default function DashboardLayout() {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const location = useLocation();

  return (
    <div className="min-h-screen bg-[#09090b]">
      {/* Mobile sidebar backdrop */}
      {sidebarOpen && (
        <div 
          className="fixed inset-0 bg-black/50 z-40 lg:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Sidebar */}
      <aside
        className={cn(
          "fixed top-0 left-0 z-50 h-full w-64 bg-[#09090b]/95 border-r border-zinc-800 transition-transform duration-300 lg:translate-x-0 glass",
          sidebarOpen ? "translate-x-0" : "-translate-x-full"
        )}
      >
        {/* Logo */}
        <div className="flex items-center gap-3 px-6 py-5 border-b border-zinc-800">
          <div className="flex items-center justify-center w-10 h-10 rounded-sm bg-cyan-500/10 border border-cyan-500/30">
            <Zap className="w-5 h-5 text-cyan-400" />
          </div>
          <div>
            <h1 className="font-mono text-lg font-bold text-white tracking-tight">WEIGHT GW</h1>
            <p className="text-xs text-zinc-500 font-mono">REST ↔ SOAP</p>
          </div>
          <Button
            variant="ghost"
            size="icon"
            className="ml-auto lg:hidden"
            onClick={() => setSidebarOpen(false)}
          >
            <X className="w-5 h-5" />
          </Button>
        </div>

        {/* Navigation */}
        <nav className="flex flex-col gap-1 p-4">
          {navigation.map((item) => {
            const isActive = location.pathname === item.href;
            return (
              <NavLink
                key={item.name}
                to={item.href}
                onClick={() => setSidebarOpen(false)}
                className={cn(
                  "flex items-center gap-3 px-3 py-2.5 rounded-sm text-sm font-medium transition-colors duration-200",
                  isActive
                    ? "bg-cyan-500/10 text-cyan-400 border border-cyan-500/30"
                    : "text-zinc-400 hover:text-white hover:bg-zinc-800/50"
                )}
                data-testid={`nav-${item.name.toLowerCase()}`}
              >
                <item.icon className="w-4 h-4" />
                {item.name}
              </NavLink>
            );
          })}
        </nav>

        {/* Connection Status */}
        <div className="absolute bottom-0 left-0 right-0 p-4 border-t border-zinc-800">
          <div className="space-y-2">
            <div className="flex items-center gap-2 text-xs">
              <Server className="w-3 h-3 text-zinc-500" />
              <span className="text-zinc-500">RODOPESO</span>
              <span className="ml-auto connection-dot configured" data-testid="rodopeso-status" />
            </div>
            <div className="flex items-center gap-2 text-xs">
              <Server className="w-3 h-3 text-zinc-500" />
              <span className="text-zinc-500">SENIOR</span>
              <span className="ml-auto connection-dot configured" data-testid="senior-status" />
            </div>
          </div>
        </div>
      </aside>

      {/* Main content */}
      <div className="lg:pl-64">
        {/* Top header */}
        <header className="sticky top-0 z-30 flex items-center gap-4 px-6 py-4 border-b border-zinc-800 bg-[#09090b]/95 glass">
          <Button
            variant="ghost"
            size="icon"
            className="lg:hidden"
            onClick={() => setSidebarOpen(true)}
            data-testid="mobile-menu-btn"
          >
            <Menu className="w-5 h-5" />
          </Button>
          
          <div className="flex-1">
            <h2 className="font-mono text-lg font-semibold text-white">
              {navigation.find(n => n.href === location.pathname)?.name || "Gateway"}
            </h2>
          </div>

          <div className="flex items-center gap-2">
            <span className="text-xs text-zinc-500 font-mono">v1.0.0</span>
          </div>
        </header>

        {/* Page content */}
        <main className="p-6">
          <Outlet />
        </main>
      </div>
    </div>
  );
}
